import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { tradeKey } from '../../utils/tradeKey';
import './TradeTags.css';

/**
 * The trader's own tags ("ORB", "VWAP fade", "revenge") and a note on closed
 * trades. Insights and RiskLo AI compare results by tag. Hidden when trade
 * insights aren't on for this person.
 */
export function useTradeTags(active = true) {
  const [tags, setTags] = useState(null); // null = unavailable

  useEffect(() => {
    if (!active) { setTags(null); return undefined; }
    let cancelled = false;
    fetch(API_ENDPOINTS.tradeInsightsTags, { headers: getBearerAuthHeaders() })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => { if (!cancelled) setTags(ok && body.success ? body.tags || {} : null); })
      .catch(() => { if (!cancelled) setTags(null); });
    return () => { cancelled = true; };
  }, [active]);

  const save = useCallback(async (key, next) => {
    const res = await fetch(API_ENDPOINTS.tradeInsightsTags, {
      method: 'PUT',
      headers: { ...getBearerAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ tradeKey: key, tags: next.tags, note: next.note }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t save the tags.');
    setTags((prev) => {
      const out = { ...(prev || {}) };
      if (body.tags.length || body.note) out[key] = { tags: body.tags, note: body.note };
      else delete out[key];
      return out;
    });
  }, []);

  return { enabled: tags !== null, tags: tags || {}, save };
}

/** Tags and note under a trade's symbol, with a small editor. */
export function TradeTagCell({ trade, account, tags, onSave }) {
  const key = tradeKey(trade, account);
  const current = tags[key] || { tags: [], note: null };
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = () => {
    setText(current.tags.join(', '));
    setNote(current.note || '');
    setError('');
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(key, { tags: text.split(',').map((t) => t.trim()).filter(Boolean), note });
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="tt">
      {current.tags.map((t) => <span key={t} className="tt-chip">{t}</span>)}
      {current.note ? <span className="tt-note" title={current.note}>“{current.note}”</span> : null}
      {!open ? (
        <button type="button" className="tt-add" onClick={start}>
          {current.tags.length || current.note ? 'Edit' : '+ Tag'}
        </button>
      ) : (
        <form className="tt-form" onSubmit={submit}>
          <input type="text" value={text} maxLength={250} placeholder="Tags, comma separated (ORB, VWAP fade…)" onChange={(e) => setText(e.target.value)} aria-label="Tags" />
          <input type="text" value={note} maxLength={1000} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} aria-label="Note" />
          <span className="tt-actions">
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => setOpen(false)}>Cancel</button>
          </span>
          {error ? <span className="tt-error">{error}</span> : null}
        </form>
      )}
    </span>
  );
}
